<template>
  <div class="page-container">
    <div class="page-header">
      <h2>疑似关联列表（领导）</h2>
      <p>查看系统识别的疑似关联，勾选后推送给助理做线下确认</p>
    </div>

    <div class="filter-section">
      <el-form inline :model="filterForm" class="filter-form">
        <el-form-item label="核心股东">
          <HolderSuggestInput v-model="filterForm.core_name" type="core" value-key="label" placeholder="汉字/拼音/首字母" style="width: 200px;" />
        </el-form-item>
        <el-form-item label="普通股东">
          <HolderSuggestInput v-model="filterForm.holder_name" type="raw" value-key="label" placeholder="汉字/拼音/首字母" style="width: 200px;" />
        </el-form-item>
        <el-form-item label="综合置信度">
          <el-radio-group v-model="filterForm.min_confidence" size="small">
            <el-radio-button :value="0">全部</el-radio-button>
            <el-radio-button :value="60">60%+</el-radio-button>
            <el-radio-button :value="70">70%+</el-radio-button>
            <el-radio-button :value="80">80%+</el-radio-button>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="filterForm.status" placeholder="请选择" style="width: 140px;">
            <el-option label="草稿" value="draft" />
            <el-option label="已推送" value="pushed" />
            <el-option label="助理已确认" value="secretary_confirmed" />
            <el-option label="助理已驳回" value="secretary_rejected" />
            <el-option label="已入控制列表" value="chairman_confirmed" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="loadList">查询</el-button>
          <el-button @click="handleReset">重置</el-button>
          <el-button type="success" :loading="refreshing" @click="handleRefresh">刷新疑似列表</el-button>
          <el-button type="warning" :disabled="selectedIds.length === 0" @click="handlePush">推送选中（{{ selectedIds.length }}）</el-button>
        </el-form-item>
      </el-form>
    </div>

    <div class="pagination-container">
      <el-pagination
        v-model:current-page="pagination.page"
        v-model:page-size="pagination.page_size"
        :page-sizes="[10, 20, 50, 100]"
        :total="pagination.total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="loadList"
        @current-change="loadList"
      />
    </div>

    <div class="table-section">
      <div v-if="loading" class="card-loading">
        <el-icon class="is-loading"><Loading /></el-icon>
      </div>
      <div v-else-if="tableData.length === 0" class="card-empty">
        <el-empty description="暂无数据" />
      </div>
      <div v-else class="card-list">
        <div v-for="row in tableData" :key="row.suspicious_id" class="suspicious-card">
          <div class="card-header">
            <div class="card-header-left">
              <el-checkbox
                v-if="row.status === 'draft'"
                :model-value="selectedIds.includes(row.suspicious_id)"
                @change="toggleSelect(row)"
              />
              <span class="card-holder-name">
                <a href="javascript:void(0)" class="holder-link" @click.stop="openHolderDetail(row.holder_name, row.core_name, 'suspicious', row, row.securities_account_no || row.account_code || row.raw_shareholder_id)">{{ row.holder_name }}</a>
              </span>
              <span class="card-account">证券账户：{{ row.raw_shareholder_id || '—' }}</span>
              <el-tag :type="statusTagType(row.status)" size="small">{{ statusLabel(row.status) }}</el-tag>
            </div>
            <div class="card-header-right">
              <template v-if="row.status === 'secretary_confirmed'">
                <el-button size="small" type="primary" @click="openChairmanConfirmDialog(row)">写入控制列表</el-button>
              </template>
              <template v-else-if="row.status === 'draft' || row.status === 'pushed'">
                <el-button size="small" type="primary" @click="openDirectConfirmDialog(row)">直接确认</el-button>
                <el-button size="small" @click="pushOne(row)">推送至助理</el-button>
              </template>
            </div>
          </div>
          <div class="card-body">
            <div class="card-body-row">
              <div class="card-field">
                <span class="field-label">推荐核心股东</span>
                <span class="field-value">
                  <a href="javascript:void(0)" class="holder-link" @click.stop="openHolderDetail(row.core_name, '')">{{ row.core_name || '—' }}</a>
                </span>
              </div>
              <div class="card-confidence-bars">
                <div class="confidence-item">
                  <span class="conf-label">地址置信</span>
                  <div class="conf-bar-wrap"><div class="conf-bar" :style="{ width: (row.address_confidence ?? 0) + '%', background: confColor(row.address_confidence) }"></div></div>
                  <span class="conf-value">{{ row.address_confidence ?? 0 }}%</span>
                </div>
                <div class="confidence-item">
                  <span class="conf-label">交易置信</span>
                  <div class="conf-bar-wrap"><div class="conf-bar" :style="{ width: (row.trade_confidence ?? 0) + '%', background: confColor(row.trade_confidence) }"></div></div>
                  <span class="conf-value">{{ row.trade_confidence ?? 0 }}%</span>
                </div>
                <div class="confidence-item">
                  <span class="conf-label">综合置信</span>
                  <div class="conf-bar-wrap"><div class="conf-bar conf-bar-strong" :style="{ width: (row.combined_confidence ?? 0) + '%', background: confColor(row.combined_confidence) }"></div></div>
                  <span class="conf-value strong">{{ row.combined_confidence ?? 0 }}%</span>
                </div>
              </div>
            </div>
            <div class="card-evidence-row">
              <div class="card-field card-field-full">
                <span class="field-label">规则证据链</span>
                <span class="field-value evidence-text">{{ row.rule_evidence || '—' }}</span>
              </div>
              <div class="card-field card-field-full">
                <span class="field-label">交易证据链</span>
                <span class="field-value">
                  <template v-if="row.trade_summary">
                    <span class="trade-evidence-link" @click="openTradeEvidenceDialog(row)">{{ tradeSummaryForTable(row.trade_summary) }}</span>
                  </template>
                  <template v-else-if="row.trade_evidence">
                    <span class="trade-evidence-link" @click="openTradeEvidenceDialog(row)">与{{ row.holder_name || '—' }}-{{ row.core_name || '—' }} 交易相似度{{ row.trade_confidence ?? 0 }}%</span>
                  </template>
                  <span v-else class="trade-miss">未命中</span>
                </span>
              </div>
            </div>
          </div>
          <div class="card-footer">
            <span class="card-create-time">创建时间：{{ row.create_time || '—' }}</span>
          </div>
        </div>
      </div>

      <div class="pagination-container">
        <el-pagination
          v-model:current-page="pagination.page"
          v-model:page-size="pagination.page_size"
          :page-sizes="[10, 20, 50, 100]"
          :total="pagination.total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="loadList"
          @current-change="loadList"
        />
      </div>

      <!-- 交易证据链展开浮层：仅展示一致信息 -->
      <el-dialog v-model="tradeEvidenceVisible" title="交易证据链" width="560px" class="trade-evidence-dialog">
        <div v-if="currentTradeRow" class="trade-evidence-body">
          <p class="trade-summary">{{ currentTradeRow.trade_summary || `与 ${currentTradeRow.holder_name} - ${currentTradeRow.core_name} 交易相似度 ${currentTradeRow.trade_confidence ?? 0}%` }}</p>
          <div v-if="tradeEvidenceConsistentBlocks.length" class="trade-consistent-list">
            <section v-for="(block, idx) in tradeEvidenceConsistentBlocks" :key="idx" class="trade-block">
              <div class="trade-block-title">{{ block.title }}</div>
              <ul class="trade-consistent-ul">
                <li v-for="(line, i) in block.lines" :key="i" class="trade-consistent-li">{{ line }}</li>
              </ul>
            </section>
          </div>
          <p v-else class="trade-no-consistent">暂无一致交易记录</p>
        </div>
        <template #footer>
          <el-button @click="tradeEvidenceVisible = false">关闭</el-button>
        </template>
      </el-dialog>
      <!-- 直接确认浮层 -->
      <el-dialog v-model="directConfirmVisible" title="直接确认" width="520px">
        <div v-if="currentConfirmRow" class="direct-confirm-body">
          <p>将以下疑似关联直接写入核心股东控制列表（等同手动添加关联）：</p>
          <p><strong>普通股东：</strong>{{ currentConfirmRow.holder_name }}</p>
          <p><strong>证券账户：</strong>{{ currentConfirmRow.raw_shareholder_id }}</p>
          <el-form-item label="核心股东" style="margin-top: 8px; margin-bottom: 0;">
            <el-select v-model="directConfirmCoreId" placeholder="请选择核心股东" filterable style="width: 100%">
              <el-option
                v-for="c in directConfirmCoreShareholders"
                :key="c.id"
                :label="c.name"
                :value="c.id"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="控制类型" style="margin-top: 12px; margin-bottom: 0;">
            <el-select v-model="directConfirmRelationType" placeholder="请选择" style="width: 100%">
              <el-option label="账户控制" value="direct" />
              <el-option label="一致行动" value="consistent" />
              <el-option label="间接控制" value="indirect" />
              <el-option label="仅关联" value="associate" />
            </el-select>
          </el-form-item>
          <p class="confirm-tip">确认后该条将进入关联列表，疑似记录状态变为「已入控制列表」。</p>
        </div>
        <template #footer>
          <el-button @click="directConfirmVisible = false">取消</el-button>
          <el-button type="primary" :loading="directConfirmLoading" @click="submitDirectConfirm">确认</el-button>
        </template>
      </el-dialog>
      <!-- 写入控制列表浮层（助理已确认 -> 领导选择控制类型后写入） -->
      <el-dialog v-model="chairmanConfirmVisible" title="写入控制列表" width="520px">
        <div v-if="chairmanConfirmRow" class="direct-confirm-body">
          <p>将以下助理已确认的疑似关联写入核心股东控制列表：</p>
          <p><strong>普通股东：</strong>{{ chairmanConfirmRow.holder_name }}</p>
          <el-form-item label="核心股东" style="margin-top: 8px; margin-bottom: 0;">
            <el-select v-model="chairmanConfirmCoreId" placeholder="请选择核心股东" filterable style="width: 100%">
              <el-option
                v-for="c in chairmanCoreShareholders"
                :key="c.id"
                :label="c.name"
                :value="c.id"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="控制类型" style="margin-top: 12px; margin-bottom: 0;">
            <el-select v-model="chairmanConfirmRelationType" placeholder="请选择" style="width: 100%">
              <el-option label="账户控制" value="direct" />
              <el-option label="一致行动" value="consistent" />
              <el-option label="间接控制" value="indirect" />
              <el-option label="仅关联" value="associate" />
            </el-select>
          </el-form-item>
          <p class="confirm-tip">确认后该条将进入关联列表，疑似记录状态变为「已入控制列表」。</p>
        </div>
        <template #footer>
          <el-button @click="chairmanConfirmVisible = false">取消</el-button>
          <el-button type="primary" :loading="chairmanConfirmLoading" @click="submitChairmanConfirm">确认写入</el-button>
        </template>
      </el-dialog>
    </div>
  </div>

  <ShareholderDetailModal
    :key="(holderDetailName || '') + '|' + (holderDetailRawShareholderId || '')"
    v-model="holderDetailVisible"
    :holder-name="holderDetailName"
    :core-name="holderDetailCoreName"
    :link-type="holderDetailLinkType"
    :suspicious-row="holderDetailSuspiciousRow"
    :raw-shareholder-id="holderDetailRawShareholderId"
  />
</template>

<script setup>
import { ref, reactive, onMounted, computed, nextTick } from 'vue'
import { ElMessage, ElIcon } from 'element-plus'
import { Loading } from '@element-plus/icons-vue'
import axios from 'axios'
import HolderSuggestInput from '@/components/HolderSuggestInput.vue'
import ShareholderDetailModal from '@/components/ShareholderDetailModal.vue'

const holderDetailVisible = ref(false)
const holderDetailName = ref('')
const holderDetailCoreName = ref('')
const holderDetailLinkType = ref(null)
const holderDetailSuspiciousRow = ref(null)
const holderDetailRawShareholderId = ref('')

function openHolderDetail(name, coreName = '', linkType = null, suspiciousRow = null, rawShareholderId = '') {
  holderDetailName.value = name || ''
  holderDetailCoreName.value = coreName || ''
  holderDetailLinkType.value = linkType
  holderDetailSuspiciousRow.value = suspiciousRow
  holderDetailRawShareholderId.value = rawShareholderId || ''
  holderDetailVisible.value = true
}

const filterForm = reactive({ core_name: '', holder_name: '', min_confidence: 0, status: 'draft' })
const tableData = ref([])
const loading = ref(false)
const refreshing = ref(false)
const selectedIds = ref([])
const pagination = reactive({ page: 1, page_size: 20, total: 0 })
const tradeEvidenceVisible = ref(false)
const currentTradeRow = ref(null)
const directConfirmVisible = ref(false)
const currentConfirmRow = ref(null)
const directConfirmCoreId = ref('')
const directConfirmLoading = ref(false)
const directConfirmRelationType = ref('direct')
const directConfirmCoreShareholders = ref([])
const chairmanConfirmVisible = ref(false)
const chairmanConfirmRow = ref(null)
const chairmanConfirmCoreId = ref('')
const chairmanConfirmLoading = ref(false)
const chairmanConfirmRelationType = ref('direct')
const chairmanCoreShareholders = ref([])
function loadList() {
  loading.value = true
  const params = {
    page: pagination.page,
    page_size: pagination.page_size,
    core_name: filterForm.core_name || undefined,
    holder_name: filterForm.holder_name || undefined,
    min_confidence: filterForm.min_confidence !== '' && filterForm.min_confidence != null ? filterForm.min_confidence : undefined,
    status: filterForm.status || undefined,
  }
  axios.get('/api/suspicious/list/', { params })
    .then(res => {
      if (res.data.status === 'ok') {
        const data = res.data.data || []
        data.sort((a, b) => (b.combined_confidence ?? 0) - (a.combined_confidence ?? 0))
        tableData.value = data
        pagination.total = res.data.total || 0
      } else {
        ElMessage.error(res.data.message || '加载失败')
      }
    })
    .catch(err => {
      ElMessage.error(err.response?.data?.message || err.message || '加载失败')
    })
    .finally(() => { loading.value = false })
}

function handleReset() {
  filterForm.core_name = ''
  filterForm.holder_name = ''
  filterForm.min_confidence = 0
  filterForm.status = 'draft'
  pagination.page = 1
  loadList()
}

function handleRefresh() {
  refreshing.value = true
  axios.post('/api/suspicious/refresh/', {})
    .then(res => {
      if (res.data.status === 'ok') {
        ElMessage.success(res.data.message || '操作成功')
        loadList()
      } else {
        ElMessage.error(res.data.message || '刷新失败')
      }
    })
    .catch(err => {
      ElMessage.error(err.response?.data?.message || err.message || '刷新失败')
    })
    .finally(() => { refreshing.value = false })
}

function onSelectionChange(rows) {
  selectedIds.value = rows.filter(r => r.status === 'draft').map(r => r.suspicious_id)
}

function handlePush() {
  if (selectedIds.value.length === 0) {
    ElMessage.warning('请勾选草稿状态的疑似关联')
    return
  }
  axios.post('/api/suspicious/push/', { suspicious_ids: selectedIds.value })
    .then(res => {
      if (res.data.status === 'ok') {
        ElMessage.success(res.data.message || '推送成功')
        selectedIds.value = []
        loadList()
      } else {
        ElMessage.error(res.data.message || '推送失败')
      }
    })
    .catch(err => {
      ElMessage.error(err.response?.data?.message || err.message || '推送失败')
    })
}

function openChairmanConfirmDialog(row) {
  chairmanConfirmRow.value = row
  chairmanConfirmRelationType.value = 'direct'
  chairmanConfirmCoreId.value = ''
  chairmanConfirmVisible.value = true
  fetchChairmanCoreShareholders().then(() => {
    let targetId = ''
    if (row.core_id) {
      const byId = chairmanCoreShareholders.value.find(c => c.id === row.core_id)
      if (byId) targetId = byId.id
    }
    if (!targetId) {
      const byName = chairmanCoreShareholders.value.find(c => c.name === (row.core_name || '').trim())
      targetId = byName ? byName.id : (chairmanCoreShareholders.value[0]?.id || '')
    }
    chairmanConfirmCoreId.value = targetId
  })
}

async function fetchChairmanCoreShareholders() {
  try {
    const res = await axios.get('/api/penetration/core-shareholders/')
    if (res.data?.status === 'ok' && res.data?.data) {
      chairmanCoreShareholders.value = res.data.data.map(c => ({ id: c.core_id, name: c.name }))
    } else {
      chairmanCoreShareholders.value = []
    }
  } catch (e) {
    chairmanCoreShareholders.value = []
  }
}

function submitChairmanConfirm() {
  if (!chairmanConfirmRow.value) return
  const rel = chairmanConfirmRelationType.value
  if (!rel || !['direct', 'consistent', 'indirect', 'associate'].includes(rel)) {
    ElMessage.warning('请选择控制类型')
    return
  }
  chairmanConfirmLoading.value = true
  axios.post('/api/suspicious/chairman_confirm/', {
    suspicious_id: chairmanConfirmRow.value.suspicious_id,
    relation_type: rel,
    core_id: chairmanConfirmCoreId.value || undefined,
  })
    .then(res => {
      if (res.data.status === 'ok') {
        ElMessage.success(res.data.message || '已写入核心股东控制列表')
        chairmanConfirmVisible.value = false
        chairmanConfirmRow.value = null
        loadList()
      } else {
        ElMessage.error(res.data.message || '操作失败')
      }
    })
    .catch(err => {
      ElMessage.error(err.response?.data?.message || err.message || '操作失败')
    })
    .finally(() => { chairmanConfirmLoading.value = false })
}

/** 表格列用：去掉末尾「，交易置信度x%」，列表已有交易置信度列 */
function tradeSummaryForTable(summary) {
  if (!summary || typeof summary !== 'string') return summary || ''
  return summary.replace(/，交易置信度\d+%$/, '')
}

/** 从 trade_evidence 全文解析出「仅一致」的区块，用于浮层展示；过滤掉不一致股东及不一致记录，列出全部一致明细 */
function parseTradeEvidenceConsistentOnly(text) {
  if (!text || typeof text !== 'string') return []
  const blocks = []
  const prefix = '交易行为匹配: '
  let s = text.startsWith(prefix) ? text.slice(prefix.length) : text
  const parts = s.split(/与已确认股东【/)
  for (let i = 1; i < parts.length; i++) {
    const part = parts[i]
    const closeBracket = part.indexOf('】')
    if (closeBracket === -1) continue
    const title = '已确认股东 ' + part.slice(0, closeBracket).trim()
    const rest = part.slice(closeBracket + 1)
    // 用正则匹配所有「【一致】...」片段（到下一个分号或结尾），确保不遗漏「一致N次:」后的第一条
    const lines = (rest.match(/【一致】[^;]+/g) || []).map(t => t.trim())
    if (lines.length) blocks.push({ title, lines })
  }
  return blocks
}

const tradeEvidenceConsistentBlocks = computed(() => {
  if (!currentTradeRow.value || !currentTradeRow.value.trade_evidence) return []
  return parseTradeEvidenceConsistentOnly(currentTradeRow.value.trade_evidence)
})

function openTradeEvidenceDialog(row) {
  currentTradeRow.value = row
  tradeEvidenceVisible.value = true
}

function openDirectConfirmDialog(row) {
  currentConfirmRow.value = row
  directConfirmRelationType.value = 'direct'
  directConfirmCoreId.value = ''
  directConfirmVisible.value = true
  fetchDirectConfirmCoreShareholders().then(() => {
    let targetId = ''
    if (row.core_id) {
      const byId = directConfirmCoreShareholders.value.find(c => c.id === row.core_id)
      if (byId) targetId = byId.id
    }
    if (!targetId) {
      const byName = directConfirmCoreShareholders.value.find(c => c.name === (row.core_name || '').trim())
      targetId = byName ? byName.id : (directConfirmCoreShareholders.value[0]?.id || '')
    }
    directConfirmCoreId.value = targetId
  })
}

async function fetchDirectConfirmCoreShareholders() {
  try {
    const res = await axios.get('/api/penetration/core-shareholders/')
    if (res.data?.status === 'ok' && res.data?.data) {
      directConfirmCoreShareholders.value = res.data.data.map(c => ({ id: c.core_id, name: c.name }))
    } else {
      directConfirmCoreShareholders.value = []
    }
  } catch (e) {
    directConfirmCoreShareholders.value = []
  }
}

function submitDirectConfirm() {
  if (!currentConfirmRow.value) return
  const rel = directConfirmRelationType.value
  if (!rel || !['direct', 'consistent', 'indirect', 'associate'].includes(rel)) {
    ElMessage.warning('请选择控制类型')
    return
  }
  directConfirmLoading.value = true
  axios.post('/api/suspicious/direct_confirm/', {
    suspicious_id: currentConfirmRow.value.suspicious_id,
    relation_type: rel,
    core_id: directConfirmCoreId.value || undefined,
  })
    .then(res => {
      if (res.data.status === 'ok') {
        ElMessage.success(res.data.message || '已写入核心股东控制列表')
        directConfirmVisible.value = false
        currentConfirmRow.value = null
        loadList()
      } else {
        ElMessage.error(res.data.message || '操作失败')
      }
    })
    .catch(err => {
      ElMessage.error(err.response?.data?.message || err.message || '操作失败')
    })
    .finally(() => { directConfirmLoading.value = false })
}

function toggleSelect(row) {
  const idx = selectedIds.value.indexOf(row.suspicious_id)
  if (idx >= 0) {
    selectedIds.value.splice(idx, 1)
  } else {
    selectedIds.value.push(row.suspicious_id)
  }
}

function statusLabel(status) {
  const map = { draft: '草稿', pushed: '已推送', secretary_confirmed: '助理已确认', secretary_rejected: '已驳回', chairman_confirmed: '已入控制列表' }
  return map[status] || status
}

function statusTagType(status) {
  const map = { draft: 'info', pushed: 'warning', secretary_confirmed: 'success', secretary_rejected: 'danger', chairman_confirmed: 'success' }
  return map[status] || 'info'
}

function confColor(val) {
  if (val >= 80) return '#67c23a'
  if (val >= 60) return '#e6a23c'
  return '#f56c6c'
}

function pushOne(row) {
  axios.post('/api/suspicious/push/', { suspicious_ids: [row.suspicious_id] })
    .then(res => {
      if (res.data.status === 'ok') {
        ElMessage.success(res.data.message || '已推送至助理')
        loadList()
      } else {
        ElMessage.error(res.data.message || '推送失败')
      }
    })
    .catch(err => {
      ElMessage.error(err.response?.data?.message || err.message || '推送失败')
    })
}

onMounted(() => {
  loadList()
  fetchDirectConfirmCoreShareholders()
  fetchChairmanCoreShareholders()
})
</script>

<style scoped>
.page-container { padding: 20px; }
.page-header { margin-bottom: 20px; }
.page-header h2 { margin: 0 0 8px 0; color: #303133; }
.page-header p { margin: 0; color: #606266; }
.filter-section { background: #fff; padding: 20px; border-radius: 4px; margin-bottom: 20px; box-shadow: 0 2px 12px 0 rgba(0,0,0,0.1); }
.table-section { background: #fff; padding: 20px; border-radius: 4px; box-shadow: 0 2px 12px 0 rgba(0,0,0,0.1); }
.pagination-container { margin-top: 20px; text-align: right; }
.trade-evidence-tooltip { white-space: pre-wrap; max-width: 420px; line-height: 1.5; }
.trade-evidence-link { color: #409eff; cursor: pointer; text-decoration: none; }
.trade-evidence-link:hover { text-decoration: underline; }
.trade-miss { color: #909399; }
.trade-evidence-dialog .trade-evidence-body { max-height: 60vh; overflow: auto; }
.trade-evidence-dialog .trade-summary { margin: 0 0 16px 0; font-weight: 500; color: #303133; }
.trade-evidence-dialog .trade-consistent-list { margin: 0; }
.trade-evidence-dialog .trade-block { margin-bottom: 16px; padding: 12px; background: #f8fafc; border-radius: 6px; border-left: 4px solid #67c23a; }
.trade-evidence-dialog .trade-block-title { font-weight: 600; color: #303133; margin-bottom: 8px; font-size: 13px; }
.trade-evidence-dialog .trade-consistent-ul { margin: 0; padding-left: 20px; }
.trade-evidence-dialog .trade-consistent-li { color: #606266; line-height: 1.6; margin-bottom: 4px; font-size: 13px; }
.trade-evidence-dialog .trade-no-consistent { color: #909399; font-size: 13px; margin: 0; }
.direct-confirm-body p { margin: 0 0 10px 0; }
.direct-confirm-body .confirm-tip { margin-top: 16px; color: #909399; font-size: 13px; }

.holder-link {
  color: #409eff;
  text-decoration: none;
  cursor: pointer;
}
.holder-link:hover {
  text-decoration: underline;
}

/* 卡片列表 */
.card-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.card-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  font-size: 24px;
  color: #409eff;
}
.card-empty {
  padding: 40px 0;
}
.suspicious-card {
  border: 1px solid #ebeef5;
  border-radius: 6px;
  background: #fff;
  transition: box-shadow 0.2s;
  overflow: hidden;
}
.suspicious-card:hover {
  box-shadow: 0 2px 12px rgba(0,0,0,0.1);
}
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #f5f7fa;
  border-bottom: 1px solid #ebeef5;
  gap: 12px;
}
.card-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}
.card-holder-name {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  white-space: nowrap;
}
.card-account {
  color: #909399;
  font-size: 13px;
  white-space: nowrap;
}
.card-header-right {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}
.card-body {
  padding: 12px 16px;
}
.card-body-row {
  display: flex;
  align-items: flex-start;
  gap: 24px;
  margin-bottom: 10px;
}
.card-field {
  display: flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
}
.card-field-full {
  flex: 1;
}
.field-label {
  color: #909399;
  font-size: 13px;
  white-space: nowrap;
  flex-shrink: 0;
}
.field-value {
  color: #303133;
  font-size: 13px;
  min-width: 0;
}
.evidence-text {
  word-break: break-all;
}
.card-confidence-bars {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 200px;
}
.confidence-item {
  display: flex;
  align-items: center;
  gap: 8px;
}
.conf-label {
  color: #909399;
  font-size: 12px;
  width: 56px;
  flex-shrink: 0;
}
.conf-bar-wrap {
  flex: 1;
  height: 6px;
  background: #ebeef5;
  border-radius: 3px;
  overflow: hidden;
}
.conf-bar {
  height: 100%;
  border-radius: 3px;
  transition: width 0.3s;
}
.conf-bar-strong {
  height: 8px;
}
.conf-value {
  color: #606266;
  font-size: 12px;
  width: 38px;
  text-align: right;
  flex-shrink: 0;
}
.conf-value.strong {
  color: #303133;
  font-weight: 600;
}
.card-evidence-row {
  display: flex;
  gap: 24px;
}
.card-footer {
  padding: 8px 16px;
  border-top: 1px solid #ebeef5;
  background: #fafafa;
}
.card-create-time {
  color: #c0c4cc;
  font-size: 12px;
}
</style>
